import IdentificationTypesSelect from '@/components/common/input/identification-types-select/IdentificationTypesSelect';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useMixPanel from '@/hooks/useMixPanel';
import { cn } from '@/lib/utils';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const ParentsForm: React.FC<{
  form: UseFormReturn<any, any, undefined>;
  formField: 'parents';
  onSave: () => Promise<void>;
  handleBlur: () => Promise<void>;
}> = ({ form, formField, onSave, handleBlur }) => {
  const Mixpanel = useMixPanel();
  const { t } = useTranslation(['newBooking', 'common']);

  const handleValidate = () => {
    onSave();
  };

  const { fields } = useFieldArray({
    control: form.control,
    name: formField
  });

  return (
    <>
      {fields.map((field, index) => {
        const parentsError = form.formState.errors?.parents;
        const isError = Array.isArray(parentsError)
          ? Boolean(parentsError[index])
          : false;

        return (
          <div key={`parent-${index}-${field.id}`}>
            <p
              className={cn(
                isError && 'text-destructive',
                'font-semibold my-5'
              )}
            >
              {t('parents', { number: index + 1 })}
            </p>
            {index === 0 && (
              <p className="mb-1 text-primary">{t('userDescription')}</p>
            )}
            <div
              className="border border-primary p-6 rounded-md flex flex-col gap-8 mb-3"
              key={'participant-participant'}
            >
              <FormField
                control={form.control}
                name={`${formField}.${index}.firstName`}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>
                        {t('stepParticipants.form.firstName')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          onClick={() =>
                            Mixpanel.track('press user name input', {
                              screen: 'New Booking',
                              organization: 'Viento en popa'
                            })
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={`${formField}.${index}.lastName`}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>
                        {t('stepParticipants.form.lastName')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          onClick={() =>
                            Mixpanel.track('press lastName input', {
                              screen: 'New Booking',
                              organization: 'Viento en popa'
                            })
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={`${formField}.${index}.connection`}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>{t('connection')}</FormLabel>
                      <FormControl>
                        <Input
                          onClick={() =>
                            Mixpanel.track('press connection input', {
                              screen: 'New Booking',
                              organization: 'Viento en popa'
                            })
                          }
                          {...field}
                          placeholder={t('connectionPlaceholder')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={`${formField}.${index}.phone`}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>{t('phone')}</FormLabel>
                      <FormControl>
                        <Input
                          onClick={() =>
                            Mixpanel.track('press user phone input', {
                              screen: 'New Booking',
                              organization: 'Viento en popa'
                            })
                          }
                          type="tel"
                          {...field}
                          placeholder={t('common:placeholder.phone')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={`${formField}.${index}.identification.type`}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <IdentificationTypesSelect
                        defaultValue={field?.value}
                        onValueChange={(value) => {
                          Mixpanel.track('press user id input', {
                            screen: 'New Booking',
                            organization: 'Viento en popa'
                          });
                          field.onChange(value);
                        }}
                        field={field}
                      />
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={`${formField}.${index}.identification.number`}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>
                        {t('stepParticipants.form.identificationNumber')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          onClick={() =>
                            Mixpanel.track(
                              'press participants id number input',
                              {
                                screen: 'New Booking',
                                organization: 'Viento en popa'
                              }
                            )
                          }
                          placeholder={t('stepParticipants.form.id')}
                          {...field}
                          value={field?.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name={`${formField}.${index}.email`}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>{t('stepParticipants.form.mail')}</FormLabel>
                      <FormControl>
                        <Input
                          onClick={() =>
                            Mixpanel.track('press user phone input', {
                              screen: 'New Booking',
                              organization: 'Viento en popa'
                            })
                          }
                          placeholder="example@email.com"
                          {...field}
                          onBlur={index === 0 ? handleBlur : undefined}
                        />
                      </FormControl>
                      <FormMessage />
                      {index === 0 && (
                        <FormDescription>
                          {t('stepParticipants.form.mail_description')}
                        </FormDescription>
                      )}
                    </FormItem>
                  );
                }}
              />
            </div>
          </div>
        );
      })}
      <Button className="w-full" type="button" onClick={handleValidate}>
        {t('common:validate')}
      </Button>
    </>
  );
};

export default ParentsForm;
